import type { ClientRole } from '@/api/types';
import { clientCan } from './access';

// The client's first-login guide: what it says per role and how it finds the places it points at.
// The page marks them with `data-tour="..."`; a step whose place is not on the screen is skipped.

export type TourStep = {
  id: string;
  /** `data-tour` value of the element the step points at. */
  anchor: string;
  title: string;
  text: string;
  /** Appended on a phone, where there is no top bar to point at. */
  phoneWhere: string;
};

const MENU = 'Раздел находится в меню ☰ вверху экрана.';
const TAB = 'Вкладка в проекте.';

const STEPS: Record<string, TourStep> = {
  tasks: {
    id: 'tasks',
    anchor: 'tab-tasks',
    title: 'Задачи',
    text: 'Всё, что команда делает по проекту: что в работе, что готово и что ждёт вас. Откройте задачу, чтобы увидеть детали.',
    phoneWhere: TAB,
  },
  waiting: {
    id: 'waiting',
    anchor: 'waiting',
    title: 'Ждём от вас',
    text: 'Здесь то, без чего работа не двинется: решить, ответить, передать доступ. Когда тут пусто, от вас ничего не нужно.',
    phoneWhere: 'Блок на странице проекта.',
  },
  inbox: {
    id: 'inbox',
    anchor: 'nav-inbox',
    title: 'Сообщения',
    text: 'Переписка с командой по всем проектам. Новые сообщения отмечены числом.',
    phoneWhere: MENU,
  },
  access: {
    id: 'access',
    anchor: 'nav-access',
    title: 'Доступы',
    text: 'Когда студии нужен доступ к сайту, рекламному кабинету или сервису, передайте его здесь, а не письмом или в мессенджере.',
    phoneWhere: MENU,
  },
  reports: {
    id: 'reports',
    anchor: 'tab-reports',
    title: 'Отчёты',
    text: 'Отчёты команды за период. Откройте нужный и скачайте его файлом.',
    phoneWhere: TAB,
  },
  money: {
    id: 'money',
    anchor: 'nav-money',
    title: 'Деньги',
    text: 'Счета и акты: что оплатить, что подписать и что уже закрыто.',
    phoneWhere: MENU,
  },
  documents: {
    id: 'documents',
    anchor: 'nav-documents',
    title: 'Документы',
    text: 'Договоры, акты и другие документы компании. Любой из них можно скачать.',
    phoneWhere: MENU,
  },
};

/** What a role is shown, in order: work people get the project, an accountant the money and documents. */
export function tourSteps(role: ClientRole | undefined): TourStep[] {
  if (!role) return [];
  const ids = clientCan(role, 'work') ? ['tasks', 'waiting', 'inbox', 'access', 'reports'] : ['money', 'documents'];
  return ids.map((id) => STEPS[id]);
}

const OPEN = 'portal:tour-open';

/** «Как пользоваться кабинетом»: any link in the portal asks the mounted guide to start again. */
export const openTour = () => window.dispatchEvent(new Event(OPEN));
export const onOpenTour = (fn: () => void) => {
  window.addEventListener(OPEN, fn);
  return () => window.removeEventListener(OPEN, fn);
};

/** Below Tailwind's `md` there is no top bar. Without `matchMedia` (tests) it is a desktop. */
export const isPhone = () => typeof window.matchMedia === 'function' && !window.matchMedia('(min-width: 768px)').matches;

/** On the screen: no ancestor is `display: none` or `hidden`, so a collapsed menu does not count. */
export function isShown(el: Element): boolean {
  for (let n: Element | null = el; n; n = n.parentElement) {
    if (n.hasAttribute('hidden') || getComputedStyle(n).display === 'none') return false;
  }
  return true;
}

export function findAnchor(name: string): HTMLElement | null {
  const all = document.querySelectorAll<HTMLElement>(`[data-tour="${name}"]`);
  return [...all].find(isShown) ?? null;
}
